import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useRealtimeEvent } from '../contexts/RealtimeContext';
import { useTheme } from '../hooks/useTheme';
import api from '../services/api';

export default function Header() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const { isDark, toggle } = useTheme();
  const [unreadCount, setUnreadCount] = useState(0);
  const [profileName, setProfileName] = useState(null);

  // Fetch profile display name on mount
  useEffect(() => {
    if (!isAuthenticated || !user?.role) return;

    async function fetchProfileName() {
      try {
        const profileEndpoints = {
          artist: '/artists/profile/me',
          promoter: '/promoters/profile/me',
          booker: '/bookers/profile/me',
        };
        const endpoint = profileEndpoints[user.role];
        if (!endpoint) return;

        const res = await api.get(endpoint);
        const profile = res.data.profile || res.data;
        // artistName pour les artistes, companyName pour promoteurs/bookers
        setProfileName(profile.artistName || profile.companyName || null);
      } catch {
        // Pas de profil encore — on garde l'email
      }
    }

    fetchProfileName();
  }, [isAuthenticated, user?.role]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await api.get('/bookings/messages/unread-count');
      setUnreadCount(res.data.unreadCount || 0);
    } catch {
      // Silent fail
    }
  }, []);

  // Fetch unread messages count on mount and poll every 60s (fallback)
  useEffect(() => {
    if (!isAuthenticated) return;
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchUnreadCount]);

  // Realtime: refresh the badge instantly when a message arrives
  useRealtimeEvent('message:new', fetchUnreadCount);

  async function handleLogout() {
    await logout();
    navigate('/');
  }

  return (
    <header className="th-bg border-b th-border transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          <Link to="/" className="font-mono font-bold text-lg tracking-[0.15em] text-accent hover:opacity-70 transition-opacity duration-150">
            STRIDERR
          </Link>

          {/* Nav desktop */}
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/artists" className="text-xs font-mono uppercase tracking-widest th-text-secondary hover:th-text transition-colors duration-150">
              Artistes
            </Link>
            <Link to="/comment-ca-marche" className="text-xs font-mono uppercase tracking-widest th-text-secondary hover:th-text transition-colors duration-150">
              Guide
            </Link>
            {isAuthenticated ? (
              <div className="flex items-center gap-4">
                <Link to="/dashboard" className="text-xs font-mono uppercase tracking-widest th-text-secondary hover:th-text transition-colors duration-150">
                  Dashboard
                </Link>
                <Link to="/messages" className="text-xs font-mono uppercase tracking-widest th-text-secondary hover:th-text transition-colors duration-150 flex items-center relative">
                  Messages
                  {unreadCount > 0 && (
                    <span className="bg-accent text-white text-[10px] font-mono px-1.5 py-0.5 ml-1">
                      {unreadCount}
                    </span>
                  )}
                </Link>
                <div className="flex items-center gap-3 pl-4 border-l th-border">
                  <span className="text-xs th-text-muted font-mono">{profileName || user.displayName || user.email}</span>
                  <button
                    onClick={handleLogout}
                    className="text-xs font-mono uppercase tracking-widest th-text-secondary hover:text-accent transition-colors duration-150"
                  >
                    Log out
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login" className="text-xs font-mono uppercase tracking-widest th-text-secondary hover:th-text transition-colors duration-150">
                  Connexion
                </Link>
                <Link
                  to="/register"
                  className="th-btn-accent"
                >
                  S'inscrire
                </Link>
              </div>
            )}
            <button
              onClick={toggle}
              className="text-xs font-mono uppercase tracking-widest th-text-secondary hover:th-text transition-colors duration-150 ml-2"
            >
              {isDark ? 'LIGHT' : 'DARK'}
            </button>
          </nav>

          {/* Mobile: theme toggle + burger */}
          <div className="flex items-center gap-3 md:hidden">
            <button
              onClick={toggle}
              className="text-xs font-mono uppercase tracking-widest th-text-secondary"
            >
              {isDark ? 'LIGHT' : 'DARK'}
            </button>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 th-text-secondary"
              aria-label="Menu"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {menuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {menuOpen && (
          <nav className="md:hidden pb-4 space-y-1 border-t th-border pt-3">
            <Link to="/artists" onClick={() => setMenuOpen(false)} className="block px-3 py-2 text-xs font-mono uppercase tracking-widest th-text-secondary hover:th-text transition-colors duration-150">
              Artistes
            </Link>
            <Link to="/comment-ca-marche" onClick={() => setMenuOpen(false)} className="block px-3 py-2 text-xs font-mono uppercase tracking-widest th-text-secondary hover:th-text transition-colors duration-150">
              Guide
            </Link>
            {isAuthenticated ? (
              <>
                <Link to="/dashboard" onClick={() => setMenuOpen(false)} className="block px-3 py-2 text-xs font-mono uppercase tracking-widest th-text-secondary hover:th-text transition-colors duration-150">
                  Dashboard
                </Link>
                <Link to="/messages" onClick={() => setMenuOpen(false)} className="flex items-center px-3 py-2 text-xs font-mono uppercase tracking-widest th-text-secondary hover:th-text transition-colors duration-150">
                  Messages
                  {unreadCount > 0 && (
                    <span className="bg-accent text-white text-[10px] font-mono px-1.5 py-0.5 ml-2">
                      {unreadCount}
                    </span>
                  )}
                </Link>
                <button
                  onClick={() => { setMenuOpen(false); handleLogout(); }}
                  className="block w-full text-left px-3 py-2 text-xs font-mono uppercase tracking-widest th-text-secondary hover:text-accent transition-colors duration-150"
                >
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setMenuOpen(false)} className="block px-3 py-2 text-xs font-mono uppercase tracking-widest th-text-secondary hover:th-text transition-colors duration-150">
                  Connexion
                </Link>
                <Link to="/register" onClick={() => setMenuOpen(false)} className="block px-3 py-2 text-xs font-mono uppercase tracking-widest text-accent">
                  S'inscrire
                </Link>
              </>
            )}
          </nav>
        )}
      </div>
    </header>
  );
}
